import React, {useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import SingleSelectNew from "../../../components/SelectField/SingleSelectNew";
import AccountOverview from "./AccountOverview/AccountOverview";
import SystemHealth from "./SystemHealth";
import { getUnreadNotificationsByCompany } from "../../../services/apiservices/notifications";
import { getIssuesByCompany } from "../../../services/apiservices/issues";
import { getDeliveredAndArchivedSupplies } from "../../../services/apiservices/supplies";
import SystemAlerts from "./SystemAlerts";
import RevenueOverview from "./RevenueOverview/RevenueOverview";
import RecentActivity from "./RecentActivity";
import RecentTransactions from "./RecentTransactions";

const SuperUserDashboard = () => {
  const [dateFilter, setDateFilter] = useState("today");
  const [alertDetails, setAlertDetails] = useState({});

  const dateOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "7days", label: "Last 7 Days" },
    { value: "30days", label: "Last 30 Days" },
    { value: "90days", label: "Last 90 Days" },
  ];

  const fetchAllAccounts = async () => {
    try {
      const [notificationRes, issueRes, suppliesRes] = await Promise.all([
        new Promise((resolve, reject) =>
          getUnreadNotificationsByCompany(resolve, reject)
        ),
        new Promise((resolve, reject) => getIssuesByCompany(resolve, reject)),
        new Promise((resolve, reject) =>
          getDeliveredAndArchivedSupplies(resolve, reject)
        ),
      ]);

      setAlertDetails({ notificationRes, issueRes, suppliesRes });
    } catch (error) {
      console.error("Failed to fetch account or revenue data", error);
    }
  };

  useEffect(() => {
    fetchAllAccounts();
  }, [dateFilter]);

  const handleDateChange = (e) => {
    setDateFilter(e);
  };

  const getFilteredData = (filter) => {
    const multiplier =
      filter === "today"
        ? 1
        : filter === "yesterday"
        ? 0.9
        : filter === "7days"
        ? 7
        : filter === "30days"
        ? 30
        : 90;

    return {
      systemStats: {
        storageUsage: `${Math.min(
          99,
          Math.floor(68 + (multiplier > 1 ? multiplier * 0.1 : 0))
        )}%`,
        storageChange: `+${Math.floor(
          5 * (multiplier > 1 ? multiplier * 0.2 : 1)
        )}%`,
        systemAlerts: Math.floor(
          12 * (multiplier > 1 ? 1 + multiplier * 0.05 : 1)
        ),
        systemAlertsChange: multiplier > 30 ? "+5" : "-3",
        supportTickets: Math.floor(
          24 * (multiplier > 1 ? 1 + multiplier * 0.03 : 1)
        ),
        supportTicketsChange: `+${Math.floor(
          4 * (multiplier > 1 ? multiplier * 0.2 : 1)
        )}`,
        unreadMessages: Math.floor(
          36 * (multiplier > 1 ? 1 + multiplier * 0.02 : 1)
        ),
        unreadMessagesChange: multiplier > 30 ? "+12" : "-8",
      },
    };
  };
  const data = getFilteredData(dateFilter);
  return (
    <div className="max-w-sm sm:max-w-4xl md:max-w-5xl lg:max-w-6xl xl max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-24 bg-[#2c3440] dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] p-16 rounded-lg text-white">
        <div className="flex items-center">
          <BarChart3 className="h-6 w-6 mr-8 text-[#9b87f5]" />
          <h1 className="text-2xl font-bold">Super Admin Dashboard</h1>
        </div>
        <div className="mt-12 sm:mt-0">
          <SingleSelectNew
            contentClass="bg-[#2c3440] text-white border-[#464c59]"
            className={"w-[180px]"}
            itemClass="hover:bg-[#3c4351]"
            value={dateFilter}
            onChange={handleDateChange}
            options={dateOptions}
          />
        </div>
      </div>

      <div className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <AccountOverview dateFilter={dateFilter} />

          <RevenueOverview dateFilter={dateFilter} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SystemHealth
            storageData={{
              value: parseFloat(data.systemStats.storageUsage.replace("%", "")),
              change: data.systemStats.storageChange,
            }}
          />

          <SystemAlerts dateFilter={dateFilter} data={alertDetails} />

          <RecentActivity dateFilter={dateFilter} />
        </div>

        <RecentTransactions dateFilter={dateFilter} />
      </div>
    </div>
  );
};
export default SuperUserDashboard;
